import { ReactNode, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import cn from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import styles from './Tooltip.module.scss';
import IconWrapper from '../Icons';

export interface TooltipProps {
  tooltipContents: ReactNode;
  children?: ReactNode;
  tooltipId: string;
  buttonClasses?: string | string[];
  popoverClasses?: string | string[];
  toggleCallback?: (toolTip: { [x: string]: boolean }) => void;
  displayCloseButton?: boolean;
  openOnHover?: boolean;
}

const Tooltip = ({
  tooltipContents,
  tooltipId,
  children,
  buttonClasses,
  popoverClasses,
  toggleCallback,
  displayCloseButton = false,
  openOnHover = false,
}: TooltipProps) => {
  const [showPopover, setShowPopover] = useState(false);
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const togglePopover = () => {
    setShowPopover((state) => !state);
    toggleCallback?.({ [tooltipId]: !showPopover });
  };
  const onOutsideClick = () => {
    if (!isHoveringButton && !isHoveringTooltip) {
      setShowPopover(false);
    }
  };

  const handleMouseEnter = () => {
    clearCloseTimeout();
    setIsHoveringButton(true);
    setShowPopover(true);
    toggleCallback?.({ [tooltipId]: true });
  };

  const handleMouseLeave = () => {
    setIsHoveringButton(false);
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      if (!isHoveringTooltip) {
        setShowPopover(false);
        toggleCallback?.({ [tooltipId]: false });
      }
    }, 200);
  };

  const handleTooltipMouseEnter = () => {
    clearCloseTimeout();
    setIsHoveringTooltip(true);
  };

  const handleTooltipMouseLeave = () => {
    setIsHoveringTooltip(false);
    if (!isHoveringButton) {
      setShowPopover(false);
      toggleCallback?.({ [tooltipId]: false });
    }
  };

  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const { styles: popperStyles, attributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    }
  );

  const PlusMinusIcon = () =>
    showPopover ? (
      <div className={cn(styles.iconButton, styles.iconClose)}>
        <IconWrapper icon="minus" fill="var(--color-mec-green)" width={20} />
      </div>
    ) : (
      <div className={cn(styles.iconButton, styles.iconOpen)}>
        <IconWrapper icon="plus" fill="var(--color-white)" width={20} />
      </div>
    );

  return (
    <OutsideClickHandler onOutsideClick={onOutsideClick}>
      <button
        type="button"
        ref={setReferenceElement}
        onClick={togglePopover}
        onMouseEnter={openOnHover ? handleMouseEnter : undefined}
        onMouseLeave={openOnHover ? handleMouseLeave : undefined}
        className={cn(styles.button, buttonClasses)}
        data-tooltip-active={showPopover}
        aria-describedby={tooltipId}
      >
        {children ?? <PlusMinusIcon />}
      </button>

      <div
        id={tooltipId}
        ref={setPopperElement}
        className={cn(styles.popover, popoverClasses)}
        style={popperStyles.popper}
        role="tooltip"
        aria-hidden={showPopover ? 'false' : 'true'}
        {...attributes.popper}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      >
        {displayCloseButton && (
          <button
            type="button"
            onClick={togglePopover}
            className={cn(styles.button, styles.close)}
          >
            <IconWrapper icon="close" />
          </button>
        )}
        {tooltipContents}
      </div>
    </OutsideClickHandler>
  );
};

export default Tooltip;
